import { Box, VStack, HStack, Text, Progress, IconButton, useColorModeValue, useBreakpointValue } from '@chakra-ui/react';
import { FiPlay, FiInfo, FiMoreVertical } from 'react-icons/fi';
import { useNavigate } from 'react-router-dom';
import IconWrapper from './IconWrapper';
import CachedImage from './CachedImage';

interface ContinueWatchingItem {
  id: string;
  title: string;
  type: string;
  thumbnail: string;
  progress: number;
  episode: string;
  duration: string;
  season?: string;
  episodeNumber?: number;
}

interface ContinueWatchingCardProps {
  item: ContinueWatchingItem;
  onPlay?: (item: ContinueWatchingItem) => void;
  onInfo?: (item: ContinueWatchingItem) => void;
}

const ContinueWatchingCard: React.FC<ContinueWatchingCardProps> = ({ item, onPlay, onInfo }) => {
  const navigate = useNavigate();
  const isMobile = useBreakpointValue({ base: true, md: false });
  
  const cardBg = useColorModeValue('white', 'gray.800');
  const titleColor = useColorModeValue('gray.900', 'white');
  const subtitleColor = useColorModeValue('gray.600', 'gray.400');
  const progressBg = useColorModeValue('gray.200', 'gray.600');

  const handlePlay = () => {
    if (onPlay) {
      onPlay(item);
    } else if (item.type === 'short') {
      navigate(`/short/${item.id}`);
    } else {
      navigate(`/content/${item.id}`);
    }
  };

  const handleInfo = () => {
    if (onInfo) {
      onInfo(item);
    } else if (item.type === 'short') {
      navigate(`/short/${item.id}`);
    } else {
      navigate(`/content/${item.id}`);
    }
  };

  return (
    <Box
      bg={cardBg}
      borderRadius="lg"
      overflow="hidden"
      boxShadow="md"
      _hover={{ boxShadow: "lg", transform: "translateY(-2px)" }}
      transition="all 0.2s ease-in-out"
      cursor="pointer"
      onClick={handlePlay}
      w={{ base: "calc(100vw - 48px)", sm: "280px", md: "320px" }}
      minW={{ base: "calc(100vw - 48px)", sm: "280px", md: "320px" }}
      maxW={{ base: "280px", sm: "280px", md: "320px" }}
    >
      {/* Thumbnail with Progress Overlay */}
      <Box position="relative" h={{ base: "140px", sm: "160px", md: "180px" }}>
        <CachedImage
          src={item.thumbnail}
          alt={item.title}
          width="100%"
          height="100%"
          objectFit="cover"
          fallbackType={item.type}
          size="w342"
        />
        
        {/* Progress Bar Overlay */}
        <Box
          position="absolute"
          bottom={0}
          left={0}
          right={0}
          h="4px"
          bg="blackAlpha.300"
        >
          <Progress
            value={item.progress}
            size="sm"
            colorScheme="purple"
            h="100%"
            bg="transparent"
            sx={{
              '& > div': {
                bg: 'purple.500'
              }
            }}
          />
        </Box>

        {/* Play Button Overlay */}
        <Box
          position="absolute"
          top="50%"
          left="50%"
          transform="translate(-50%, -50%)"
          bg="blackAlpha.600"
          borderRadius="full"
          p={2}
          backdropFilter="blur(4px)"
          _hover={{ bg: "blackAlpha.700" }}
          onClick={(e) => {
            e.stopPropagation();
            handlePlay();
          }}
        >
          <IconWrapper icon={FiPlay} size={20} color="white" />
        </Box>

        {/* Action Buttons */}
        <HStack
          position="absolute"
          top={2}
          right={2}
          gap={1}
        >
          <IconButton
            aria-label="More options"
            icon={<IconWrapper icon={FiMoreVertical} size={16} />}
            size="sm"
            variant="ghost"
            bg="blackAlpha.600"
            color="white"
            borderRadius="full"
            _hover={{ bg: "blackAlpha.700" }}
            onClick={(e) => {
              e.stopPropagation();
              // Handle more options
            }}
          />
        </HStack>

        {/* Episode Badge */}
        {item.episodeNumber && (
          <Box
            position="absolute"
            top={2}
            left={2}
            bg="purple.500"
            color="white"
            px={2}
            py={1}
            borderRadius="md"
            fontSize="xs"
            fontWeight="semibold"
          >
            E{item.episodeNumber}
          </Box>
        )}
      </Box>

      {/* Content Info */}
      <VStack align="stretch" p={4} gap={2}>
        <VStack align="start" gap={1}>
          <Text
            fontSize="md"
            fontWeight="semibold"
            color={titleColor}
            noOfLines={2}
            lineHeight="1.3"
          >
            {item.title}
          </Text>
          
          <HStack justify="space-between" w="100%">
            <Text fontSize="sm" color={subtitleColor}>
              {item.episode}
            </Text>
            <Text fontSize="sm" color={subtitleColor}>
              {item.duration}
            </Text>
          </HStack>
        </VStack>

        {/* Progress Info */}
        <VStack align="stretch" gap={1}>
          <HStack justify="space-between" w="100%">
            <Text fontSize="xs" color={subtitleColor}>
              {item.progress}% watched
            </Text>
            <Text fontSize="xs" color={subtitleColor}>
              {item.season && `${item.season} • `}Continue
            </Text>
          </HStack>
          
          <Progress
            value={item.progress}
            size="sm"
            colorScheme="purple"
            bg={progressBg}
            borderRadius="full"
          />
        </VStack>

        {/* Action Buttons */}
        <HStack justify="space-between" pt={2}>
          <IconButton
            aria-label="More info"
            icon={<IconWrapper icon={FiInfo} size={16} />}
            size="sm"
            variant="outline"
            colorScheme="purple"
            onClick={(e) => {
              e.stopPropagation();
              handleInfo();
            }}
          />
          
          <Text fontSize="xs" color={subtitleColor} alignSelf="center">
            Tap to continue
          </Text>
        </HStack>
      </VStack>
    </Box>
  );
};

export default ContinueWatchingCard;
